import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { loucaApi } from '../services/loucaApi';
import { useAuth } from '../contexts/AuthContext';

const ComparisonPage = () => {
  const [inspections, setInspections] = useState([]);
  const [selected1, setSelected1] = useState(null);
  const [selected2, setSelected2] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    loadInspections();
  }, []);

  const loadInspections = async () => {
    try {
      const response = await loucaApi.getInspectedCars(user?.user_id || 'U123');
      if (response.status === 'success') {
        setInspections(response.data.inspections || []);
      }
    } catch (err) {
      console.error('Error loading inspections:', err);
    }
  };

  const handleCompare = async () => {
    if (!selected1 || !selected2) {
      alert('Veuillez sélectionner 2 inspections');
      return;
    }

    setLoading(true);
    try {
      const response = await loucaApi.compare({
        inspection_id_1: selected1,
        inspection_id_2: selected2,
        owner: { type: 'driver', id: user?.user_id || 'U123' },
        user_id: user?.user_id || 'U123',
        mode: 'auto',
      });

      if (response.status === 'success') {
        setComparison(response.data);
      }
    } catch (err) {
      console.error('Comparison error:', err);
      alert('Erreur lors de la comparaison');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <Layout>
      <div className="pb-24 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-6">Comparer des Inspections</h1>

        {/* Selection */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Inspection 1 (Ancienne)</h2>
            <select
              value={selected1 || ''}
              onChange={(e) => setSelected1(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner...</option>
              {inspections
                .filter((i) => i.status === 'completed')
                .map((inspection) => (
                  <option key={inspection.inspection_id} value={inspection.inspection_id}>
                    {inspection.inspection_id} - {formatDate(inspection.created_at)}
                  </option>
                ))}
            </select>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Inspection 2 (Récente)</h2>
            <select
              value={selected2 || ''}
              onChange={(e) => setSelected2(e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sélectionner...</option>
              {inspections
                .filter((i) => i.status === 'completed')
                .map((inspection) => (
                  <option key={inspection.inspection_id} value={inspection.inspection_id}>
                    {inspection.inspection_id} - {formatDate(inspection.created_at)}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <button
          onClick={handleCompare}
          disabled={loading || !selected1 || !selected2}
          className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
        >
          {loading ? 'Analyse en cours...' : 'Comparer'}
        </button>

        {/* Results */}
        {comparison && (
          <div className="mt-8 space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Résultats de la Comparaison</h2>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4">
                  <p className="text-green-400 font-semibold text-sm mb-1">Nouveaux dommages</p>
                  <p className="text-2xl font-bold text-white">{comparison.new_damages || 0}</p>
                </div>
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4">
                  <p className="text-blue-400 font-semibold text-sm mb-1">Dommages réparés</p>
                  <p className="text-2xl font-bold text-white">{comparison.repaired_damages || 0}</p>
                </div>
                <div className="bg-slate-500/20 border border-slate-500/50 rounded-lg p-4">
                  <p className="text-slate-400 font-semibold text-sm mb-1">Inchangés</p>
                  <p className="text-2xl font-bold text-white">{comparison.unchanged || 0}</p>
                </div>
              </div>

              {comparison.differences && comparison.differences.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-white mb-3">Détails des Différences</h3>
                  {comparison.differences.map((diff, index) => (
                    <div key={index} className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">{diff.field}</h4>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-400 mb-1">Avant:</p>
                          <p className="text-white">{diff.before}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 mb-1">Après:</p>
                          <p className="text-white">{diff.after}</p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            diff.severity === 'aggravation'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-green-500/20 text-green-400'
                          }`}
                        >
                          {diff.severity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ComparisonPage;



